"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Loader, Upload, FileText, Link as LinkIcon, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function SitemapInputForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="36583" className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in duration-500">
        <div className="rounded-full bg-primary/10 p-6 border border-primary/20">
          <FileText className="size-10 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-serif text-foreground">{successMessage}</h3>
          <p className="text-muted-foreground font-sans text-sm">Our patient coordinator will contact you shortly to confirm your appointment.</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white uppercase tracking-wider font-semibold rounded-full px-8">
          Schedule Another
        </Button>
      </div>;
  }
  const inputStyles = "bg-transparent border-0 border-b border-primary/20 rounded-none px-0 py-6 text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-[0_0_15px_rgba(var(--primary),0.1)] transition-all duration-300 font-sans";
  const labelStyles = "text-foreground/80 font-sans text-xs uppercase tracking-widest";
  return <Form {...{
    control,
    handleSubmit
  } as any} onSubmit={handleSubmit} className="space-y-8 pt-4">
      <FormField control={control} name="sitemap_url" rules={{
      required: "Full name is required",
      pattern: {
        value: /^[a-zA-Z\s]+$/,
        message: "Please enter a valid name"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelStyles}>Full Name</FormLabel>
            <FormControl>
              <div className="relative">
                <LinkIcon className="absolute left-0 top-1/2 -translate-y-1/2 size-4 text-primary" />
                <Input placeholder="John Doe" className={`${inputStyles} pl-8`} {...field} />
              </div>
            </FormControl>
            <FormMessage className="text-destructive font-sans text-xs" />
          </FormItem>} />

      <FormField control={control} name="sitemap_file" render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem>
            <FormLabel className={labelStyles}>Upload Previous Records (Optional)</FormLabel>
            <FormControl>
              <div className="relative group cursor-pointer">
                <Input type="file" className={`${inputStyles} file:bg-primary file:text-white file:border-0 file:mr-4 file:px-4 file:py-1 file:text-xs file:font-semibold file:uppercase file:tracking-wider hover:file:bg-primary/90 hover:file:text-white transition-colors cursor-pointer`} accept=".xml,.txt" onChange={e => {
            onChange(e.target.files?.[0]);
          }} {...field} />
                <Upload className="absolute right-0 top-1/2 -translate-y-1/2 size-4 text-primary opacity-50 group-hover:opacity-100 transition-opacity" />
              </div>
            </FormControl>
            <FormMessage className="text-destructive font-sans text-xs" />
          </FormItem>} />

      <FormField control={control} name="description" render={({
      field
    }) => <FormItem>
            <FormLabel className={labelStyles}>Your Smile Goals</FormLabel>
            <FormControl>
              <textarea placeholder="Tell us about your needs (e.g. Dentures, Implants, or Extractions)..." className={`${inputStyles} min-h-[100px] resize-none`} {...field} />
            </FormControl>
            <FormMessage className="text-destructive font-sans text-xs" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-destructive text-sm font-sans">{submitError}</p>
        </div>}

      <Button asChild className="w-full bg-primary text-white hover:bg-primary/90 hover:shadow-lg rounded-full h-14 uppercase font-bold tracking-widest text-sm transition-all duration-300 mt-4">
        <Link to="tel:626-268-5990">
          Request Consultation
          <ArrowRight className="ml-2 size-4" />
        </Link>
      </Button>
    </Form>;
}
export default function SitemapInput() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="schedule-your-consultation" className="relative w-full bg-secondary py-[150px] overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[300px] h-[300px] bg-primary/5 rounded-full blur-[80px]" />
      </div>

      <div className="container mx-auto px-6 md:px-12 relative z-10">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-[60%_40%] gap-16 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="space-y-8 pr-0 lg:pr-12">
            <div className="space-y-4">
              <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl text-foreground leading-[1.1]">
                Start Your <br />
                <span className="text-primary">New Smile</span>
              </h2>
              <div className="w-24 h-1 bg-primary" />
            </div>
            
            <div className="space-y-6 text-foreground/80 font-sans text-lg leading-relaxed font-light">
              <p>
                Take the first step toward a confident smile with a comprehensive consultation at our El Monte clinic. Our expert team utilizes advanced diagnostic imaging to assess your oral health and recommend the most effective solutions.
              </p>
              <p>
                From affordable dentures to high-quality dental implants and gentle extractions, we prioritize your comfort and long-term satisfaction. Experience compassionate care that fits your budget and lifestyle.
              </p>
            </div>

            <div className="flex items-center gap-4 pt-4">
              <div className="flex -space-x-4">
                {[1, 2, 3].map(i => <div key={i} data-index={i} className="w-12 h-12 rounded-full border-2 border-secondary bg-white flex items-center justify-center text-primary shadow-sm">
                    <Loader className="size-5 animate-spin-slow" style={{
                  animationDuration: `${i * 2}s`
                }} />
                  </div>)}
              </div>
              <span className="text-foreground/60 font-sans text-sm uppercase tracking-wider">
                Care Team Ready
              </span>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-b from-primary/10 to-transparent blur-sm rounded-2xl opacity-50" />
            <Card className="bg-card border border-border rounded-2xl shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-50" />
              
              <CardContent className="p-8 md:p-10">
                <div className="mb-6">
                  <h3 className="text-foreground font-serif text-2xl mb-2">Request Appointment</h3>
                  <p className="text-muted-foreground font-sans text-xs uppercase tracking-widest">Select Your Preferred Options</p>
                </div>

                <FormProvider formKey="consultation_form_implants" formId="consultation_form_implants" sectionName="schedule-your-consultation" defaultValues={{
                sitemap_url: "",
                sitemap_file: undefined,
                description: ""
              }} validationRules={{
                sitemap_url: {
                  required: true,
                  pattern: "text"
                }
              }} submitText="REQUEST CONSULTATION" successMessage="Your request has been received. We look forward to seeing you!" errorMessage="Something went wrong. Please call our office directly to schedule.">
                  <SitemapInputForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}